import { useEffect, useState } from "react";
import { Save, MessageSquareCode, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const PROVIDERS = [
  { id: "builtin", label: "Built-in (Rule-based)", hint: "Tara Assist decision tree (default). Captures leads to your dashboard." },
  { id: "tawk", label: "Tawk.to", hint: "Enter your Tawk Property ID and Widget ID like: 6XXXXXXXXXX/1abcdefgh" },
  { id: "crisp", label: "Crisp", hint: "Enter your Crisp Website ID (uuid format)." },
  { id: "intercom", label: "Intercom", hint: "Enter your Intercom App ID." },
  { id: "custom", label: "Custom Embed", hint: "Paste any provider's full embed snippet (script tags allowed)." },
  { id: "off", label: "Disabled", hint: "Hide chat widget entirely." },
];

export default function AdminChatWidget() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Chat widget configuration saved");
      refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="skeleton h-96" />;

  const provider = form.chat_widget_provider || "builtin";
  const selected = PROVIDERS.find((p) => p.id === provider);

  return (
    <div data-testid="admin-chat-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="heading-xl text-3xl flex items-center gap-3">
            <MessageSquareCode className="w-8 h-8 text-[#E65C00]" /> CHAT WIDGET
          </h1>
          <p className="text-gray-600 text-sm mt-1">Choose your customer-chat provider. Plug in API keys from Tawk.to, Crisp, Intercom — or paste any custom embed snippet.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="chat-widget-save">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 mb-8">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            onClick={() => setForm({ ...form, chat_widget_provider: p.id })}
            className={`text-left p-5 border-2 transition-colors ${provider === p.id ? "border-[#E65C00] bg-[#E65C00]/5" : "border-gray-200 hover:border-gray-400 bg-white"}`}
            data-testid={`provider-${p.id}`}
          >
            <div className="font-heading font-black uppercase tracking-tight">{p.label}</div>
            <div className="text-xs text-gray-600 mt-2 leading-relaxed">{p.hint}</div>
          </button>
        ))}
      </div>

      {selected && provider !== "builtin" && provider !== "off" && (
        <div className="bg-white border border-gray-200 p-7 max-w-3xl">
          <div className="font-heading font-black uppercase text-lg mb-1">{selected.label} configuration</div>
          <div className="text-sm text-gray-600 mb-5">{selected.hint}</div>

          {provider === "tawk" && (
            <>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Property ID / Widget ID</label>
              <input
                placeholder="e.g. 60c1234abcde7f1234567890/1h0abc1de"
                className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
                value={form.chat_widget_key || ""}
                onChange={(e) => setForm({ ...form, chat_widget_key: e.target.value })}
                data-testid="chat-widget-key"
              />
              <a href="https://dashboard.tawk.to" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#E65C00] hover:underline mt-2">
                Open Tawk.to Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}

          {provider === "crisp" && (
            <>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Crisp Website ID</label>
              <input
                placeholder="e.g. abc12345-def6-789g-h0ij-klmnopqrstuv"
                className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
                value={form.chat_widget_key || ""}
                onChange={(e) => setForm({ ...form, chat_widget_key: e.target.value })}
                data-testid="chat-widget-key"
              />
              <a href="https://app.crisp.chat" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#E65C00] hover:underline mt-2">
                Open Crisp Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}

          {provider === "intercom" && (
            <>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Intercom App ID</label>
              <input
                placeholder="e.g. abcd1234"
                className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
                value={form.chat_widget_key || ""}
                onChange={(e) => setForm({ ...form, chat_widget_key: e.target.value })}
                data-testid="chat-widget-key"
              />
              <a href="https://app.intercom.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#E65C00] hover:underline mt-2">
                Open Intercom Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}

          {provider === "custom" && (
            <>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Custom Embed Snippet (HTML / JS)</label>
              <textarea
                rows={10}
                placeholder={'<!-- Paste your full provider script tag here -->\n<script type="text/javascript">\n...\n</script>'}
                className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-xs font-mono"
                value={form.chat_widget_code || ""}
                onChange={(e) => setForm({ ...form, chat_widget_code: e.target.value })}
                data-testid="chat-widget-code"
              />
              <div className="text-[10px] text-gray-500 mt-2">Note: Inline &lt;script&gt; tags will be executed. Only paste code from trusted providers.</div>
            </>
          )}
        </div>
      )}

      {provider === "builtin" && (
        <div className="bg-[#FFCC00]/10 border-l-4 border-[#FFCC00] p-5 max-w-3xl">
          <div className="font-heading font-bold uppercase text-sm">Built-in Chatbot Active</div>
          <div className="text-sm text-gray-700 mt-1">Tara Assist (rule-based decision tree) is running. All leads land in the <strong>Leads</strong> tab.</div>
        </div>
      )}
    </div>
  );
}
